import { USER_EMAIL, USER_ID } from '~/constants/storages';
import { cookieService } from '~/services/cookie-service';
import { logger } from '~/utils/logger';

type GtagCommand = 'event' | 'config' | 'set' | 'get' | 'consent';

interface EventParams {
  [key: string]: any;
}

interface ConfigParams {
  [key: string]: any;
}

interface ConsentParams {
  ad_storage?: 'granted' | 'denied';
  analytics_storage?: 'granted' | 'denied';
  ad_user_data?: 'granted' | 'denied';
  ad_personalization?: 'granted' | 'denied';
  wait_for_update?: number;

  [key: string]: any;
}

export function googleDataLayerPush(command: 'event', eventName: string, eventParams?: EventParams): void;
export function googleDataLayerPush(command: 'config', targetId: string, configParams?: ConfigParams): void;
export function googleDataLayerPush(command: 'set', settings: Record<string, any>): void;
export function googleDataLayerPush(command: 'set', name: string, value: any): void;
export function googleDataLayerPush(
  command: 'get',
  targetId: string,
  fieldName: string,
  callback: (value: any) => void,
): void;
export function googleDataLayerPush(
  command: 'consent',
  consentArg: 'default' | 'update',
  consentParams: ConsentParams,
): void;
export function googleDataLayerPush(command: GtagCommand, ...rest: any[]): void {
  try {
    const userId = localStorage.getItem(USER_ID);
    const userEmail = localStorage.getItem(USER_EMAIL);

    window.dataLayer = window.dataLayer || [];

    if (userId) {
      window.dataLayer.push({
        user_id: String(userId),
      });
    }

    if (userEmail) {
      window.dataLayer.push({
        user_data: {
          email_address: userEmail,
        },
      });
    }

    switch (command) {
      case 'event': {
        const [eventName, eventParams = {}] = rest;
        window.dataLayer.push({
          event: eventName,
          ...eventParams,
        });
        break;
      }
      case 'config': {
        const [targetId, configParams = {}] = rest;
        window.dataLayer.push({
          command: 'config',
          target: targetId,
          ...configParams,
        });
        break;
      }
      case 'set': {
        if (typeof rest[0] === 'object') {
          window.dataLayer.push({
            command: 'set',
            ...rest[0],
          });
        } else {
          const [name, value] = rest;
          window.dataLayer.push({
            command: 'set',
            [name]: value,
          });
        }
        break;
      }
      case 'get': {
        const [targetId, fieldName, callback] = rest;
        window.dataLayer.push({
          command: 'get',
          target: targetId,
          field_name: fieldName,
          callback: callback,
        });
        break;
      }
      case 'consent': {
        const [consentArg, consentParams = {}] = rest;
        window.dataLayer.push({
          command: 'consent',
          consent_arg: consentArg,
          ...consentParams,
        });
        break;
      }
      default: {
        window.dataLayer.push({
          command,
          ...rest,
        });
      }
    }
  } catch (error) {
    // Log the error but don't let it crash the application
    logger.error('Error in GTM tracking:', error);
  }
}

// constants
const GA_CLIENT_ID = 'x-ga-client-id';

// events
const updateUserId = (user_id) => {
  if (window.dataLayer) {
    window.dataLayer.push({ user_id: String(user_id) });
  }
};

const logOut = () => googleDataLayerPush('event', 'log_out');

const startCheckout = async ({ transactionId, fullPrice, currency, itemId, subscriptionPeriod }: any) =>
  new Promise((res: any) => {
    googleDataLayerPush('event', 'start_checkout', {
      transaction_id: transactionId,
      value: fullPrice,
      currency: currency,
      item: {
        item_id: itemId,
        price: fullPrice,
        quantity: 1,
      },
      payment_type: 'stripe',
      subscription_period: subscriptionPeriod,
      event_callback: () => {
        res();
      },
    });
    setTimeout(() => res(), 3000);
  });

const successPaymentPage = ({ userId, email }: { userId: string; email: string }) => {
  googleDataLayerPush('event', 'success_page', {
    user_id: userId,
    user_data: {
      email_address: email,
    },
  });
};

// methods
function getGa4ClientId() {
  try {
    const cookie = {};
    document.cookie.split(';').forEach(function (el) {
      const splitCookie = el.split('=');
      const key = splitCookie[0].trim();
      const value = splitCookie[1];
      cookie[key] = value;
    });
    return cookie['_ga']?.substring(6);
  } catch (error) {
    logger.error('getGa4ClientId', error);
    return '';
  }
}

// exported modules
export const googleAnalytics = {
  events: {
    updateUserId,
    logOut,
    startCheckout,
    successPaymentPage,
  },
  methods: {
    getGa4ClientId,
  },
  constants: {
    GA_CLIENT_ID,
  },
};

const GA_MEASUREMENT_ID = process.env.GA_MEASUREMENT_ID;
window.googleAnalytics = googleAnalytics;
setTimeout(() => {
  window.cookieService = cookieService;
}, 1000);
window.GA_MEASUREMENT_ID = GA_MEASUREMENT_ID;
